import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Button } from "@app/scripts/react-general-components/Button";
import { MediaImage } from "@app/scripts/react-general-components/MediaImage";
import { useShopifyProduct } from "@app/scripts/react-hooks/shopify";
import { ProductSizeSelect } from "@app/scripts/react-product-details/components/ProductSizeSelect";
import { ProductPrice } from "@app/scripts/react-product/components/ProductPrice";
import { shopify } from "@app/scripts/utils/graphql";
import { GET_VARIANT_STOCK_LEVEL } from "@app/scripts/utils/queries";
import { CartQty } from "@app/scripts/react-cart/components/CartQty";
import { CartProductStockStatus } from "@app/scripts/react-cart/components/CartProductStockStatus";
import { CartNotifyForm } from "@app/scripts/react-cart/components/CartNotifyForm";

import type { Cart, LineItem, NormalisedProduct } from "@app/scripts/types";

type CartProductProps = {
  lineItem: LineItem;
  products: NormalisedProduct[];
  handleUpdate: (variantId: number, qty: number, refresh?: boolean) => Promise<void>;
  cart: Cart;
  addOosVariant: (variantId: number) => void;
  giftId?: string | number;
  giftMessage?: string | null;
  lowStockThreshold: number;
  notifyLabel: string;
  giftIds?: number[];
};

export const CartProduct: React.FC<CartProductProps> = ({
  lineItem,
  products,
  handleUpdate,
  cart,
  addOosVariant,
  giftId,
  giftMessage,
  lowStockThreshold,
  notifyLabel,
  giftIds,
}) => {
  const { getColor, getBadges } = useShopifyProduct({});
  const [loading, setLoading] = useState(false);
  const [oos, setOos] = useState(false);
  const [maxQty, setMaxQty] = useState<number | null>(null);
  const [selectedVariant, setSelectedVariant] = useState<number>(lineItem.variant_id);

  const [isNotifyFormShowing, setIsNotifyFormShowing] = useState<boolean>(false);

  const isGift = lineItem?.properties?.isGWP === "true" && giftIds?.includes(lineItem.product_id)

  const matchingProduct = useMemo(() => products.find((p) => p.id === lineItem.product_id), [products, lineItem]);
  const color = useMemo(() => getColor(matchingProduct), [matchingProduct, getColor]);
  const badges = useMemo(() => getBadges(matchingProduct), [matchingProduct, getBadges]);
  const title = lineItem?.title?.split(/-.*-/)[0];

  const onSizeChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    setLoading(true);
    const newVariantId = parseInt(e.target.value);
    const itemInCart = cart?.items.find(
      (item) => item.variant_id === newVariantId && item.variant_id !== lineItem.variant_id
    );
    const qty = lineItem.quantity + (itemInCart?.quantity ?? 0);

    const updatedOos = await fetchVariantStock(newVariantId);
    setOos(updatedOos);

    if (!updatedOos) {
      await handleUpdate(lineItem.variant_id, 0, false);
      await handleUpdate(newVariantId, qty);

      setIsNotifyFormShowing(false);
    } else {
      setSelectedVariant(Number(newVariantId));
    }

    setLoading(false);
  };

  const onQtyChange = useCallback(
    async (value: number) => {
      setLoading(true);
      await handleUpdate(lineItem.variant_id, value);
      setLoading(false);
    },
    [setLoading, handleUpdate, lineItem.variant_id]
  );

  const removeItem = useCallback(async () => {
    // if manually removed, store this in local storage to prevent auto adding back
    if (isGift) {
      localStorage.setItem(`gift_removed:${lineItem.product_id}`, "true");
    }
    onQtyChange(0);
  }, [onQtyChange, lineItem.product_id, isGift]);

  const fetchVariantStock = useCallback(async (variantId: number) => {
    const data = await shopify(GET_VARIANT_STOCK_LEVEL, {
      nodeId: btoa("gid://shopify/ProductVariant/" + variantId),
    });

    setMaxQty(data?.node?.quantityAvailable || null);

    if (data?.node?.availableForSale === false && data?.node?.currentlyNotInStock === false) {
      addOosVariant(lineItem.variant_id);
      return true;
    } else {
      return false;
    }
  }, []);

  useEffect(() => {
    fetchVariantStock(lineItem.variant_id);
  }, []);

  return (
    <div className="cart__line-item-wrapper">
      <div
        className={`cart__line-item ${oos ? "is-oos" : ""}`}
        style={{ opacity: loading ? 0.6 : undefined, pointerEvents: loading ? "none" : undefined }}
      >
        {matchingProduct?.media?.[0] ? (
          <a href={lineItem.url} title={title} className="block">
            <MediaImage media={matchingProduct?.media?.[0]} />
          </a>
        ) : (
          <div className="cart__line-item__placeholder" />
        )}

        <div className="cart__line-item__content">
          <div className="cart__line-item__content-details">
            <div>
              <h3 className="mb-2">
                <a href={lineItem.url} title={title} className="cart__line-item__title text-md bold">
                  {title}
                </a>
              </h3>
              {!isGift && badges && badges.length > 0 && (
                <div className="cart__line-item__badges">
                  {badges?.map((badge) => (
                    <span className="badge" data-flagcolor={badge.tag}>
                      {badge.title.replaceAll("-", " ")}
                    </span>
                  ))}
                </div>
              )}
              {color && <div className="text-sm text-neutral-secondary-grey caps">{color}</div>}
            </div>

            {!isGift ? (
              <ProductPrice product={matchingProduct} lineItem={lineItem} />
            ) : (
              <span className="text-md bold">Free</span>
            )}
          </div>

          {isGift && giftMessage && <div className="cart__line-item__gift-message text-sm">{giftMessage}</div>}

          <div className="cart__line-item__options">
            {matchingProduct && (
              <ProductSizeSelect
                variants={matchingProduct.variants}
                selectedId={lineItem.variant_id}
                handleChangeVariant={onSizeChange}
              />
            )}

            {oos ? (
              <a className="find-in-store-wrapper" href={lineItem.url}>
                <Button variant="text" label={"Find In Store"} ariaLabel={"Find In Store"} />
                <ui-icon name="icon-redirect" />
              </a>
            ) : (
              !isGift && <CartQty initialValue={lineItem.quantity} onQtyChange={onQtyChange} />
            )}
          </div>

          <div className="cart__line-item__bottom-wrap">
            {(oos || maxQty === lowStockThreshold) && (
              <CartProductStockStatus status={oos ? "out-of-stock" : "low-stock"} />
            )}

            {/* {oos && (
              <Button
                variant="small"
                color="grey"
                label="Notify Me"
                className="mt-0 cart__line-item__notify"
                onClick={() => setIsNotifyFormShowing(true)}
              />
            )} */}

            <div className="cart__line-item__bottom">
              <Button variant="text" onClick={removeItem} label={"Remove"} ariaLabel={"Remove"} data-variant={lineItem.variant_id} />
            </div>
          </div>
        </div>
      </div>

      {isNotifyFormShowing && (
        <CartNotifyForm variantId={selectedVariant} productId={lineItem.product_id} notifyLabel={notifyLabel} />
      )}
    </div>
  );
};
